import React, { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { isMobile, isTablet } from "react-device-detect";

import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";

import InfoBox from "components/InfoBox";
import StyledModal from "components/Modal";

import { useAppDispatch } from "store/hooks";
import {
  checkMonobankIntegration,
  completeMonobankIntegration,
  getBankIntegrations,
} from "store/features/bankIntegrationsSlice";

import { BankTokenAddMonobankResponseDto } from "api";

import MonobankResultModal, {
  MonobankResultType,
} from "../MonobankResultModal";

interface MonobankModalProps {
  isOpen: boolean;
  onClose: () => void;
  monobankInfo: BankTokenAddMonobankResponseDto | null;
}

const MonobankModal: React.FC<MonobankModalProps> = (props) => {
  const { monobankInfo, isOpen, onClose } = props;
  const url = monobankInfo?.monobankAcceptUrl || "";
  const bankTokenUid = monobankInfo?.bankTokenUid || "";

  const dispatch = useAppDispatch();

  const [errorMessage, setErrorMessage] = useState("");

  const [showResultModal, setShowResultModal] = useState(false);
  const [responseType, setResponseType] = useState<MonobankResultType>(
    MonobankResultType.ERROR
  );

  const onSubmit = async () => {
    setErrorMessage("");

    try {
      const { isConfirmed } = await dispatch(
        checkMonobankIntegration(bankTokenUid)
      ).unwrap();

      if (isConfirmed) {
        await dispatch(completeMonobankIntegration({ bankTokenUid })).unwrap();
        setResponseType(MonobankResultType.SUCCESS);
        setShowResultModal(true);
        onClose();
      } else {
        setErrorMessage(
          "Підключення не підтверджено. Будь ласка, спробуйте ще раз."
        );
      }
    } finally {
      await dispatch(getBankIntegrations()).unwrap();
    }
  };

  const onCloseModal = () => {
    onClose();
  };

  return (
    <>
      <StyledModal
        isOpen={isOpen}
        onClose={onCloseModal}
        title="Підключити Monobank"
        subtitle={
          isMobile || isTablet
            ? "Якщо ви підтвердили доступ у додатку Monobank — натисніть «Підтвердити доступ."
            : "Проскануйте QR код додатком від Монобанк, щоб надати доступ до виписки. Потім поверніться сюди та натисніть «Підтвердити доступ»."
        }
        content={
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 3,
            }}
          >
            {isMobile || isTablet ? (
              <InfoBox
                title="Інфо"
                content={
                  <>
                    Якщо вас не було перенаправлено у додаток Monobank, натисніть «Відкрити додаток» та підтвердьте доступ до виписки.
                    Потім поверніться на цю сторінку і натисніть «Підтвердити доступ».
                    <br />
                    <br />
                    <Link href={url}>Відкрити додаток</Link>
                  </>
                }
              />
            ) : (
              <QRCodeSVG value={url} size={260} level="Q" />
            )}
            {errorMessage && (
              <Typography color="red800">{errorMessage}</Typography>
            )}
          </Box>
        }
        onAction={onSubmit}
        actionBtnText="Підтвердити доступ"
      />
      <MonobankResultModal
        type={responseType}
        isOpen={showResultModal}
        onClose={() => setShowResultModal(false)}
      />
    </>
  );
};

export default MonobankModal;
